import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import _ from "lodash";
import { Icon, Flex } from "@mm/ui";
import { useViewport } from "@core/theme";

import dataLayer from "helper/dataLayer";

import { useFilterProvider, SET_PARAM } from "../FilterProvider";

import FilterSvg from "./filter.svg";

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ColFilter = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  cursor: initial;
  font-weight: bold;
`;

const ColFilterIcon = styled.div`
  margin-right: ${(props) => props.theme.space.s}px;
  position: relative;

  &:hover {
    cursor: pointer;
  }
`;

const ColFilterIconActivePoint = styled.div`
  display: block;
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #df0100;
  width: 8px;
  height: 8px;
  border-radius: 40px;
`;

const ColDisplay = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ColDisplaySortContainer = styled.div`
  font-weight: normal;
`;
const ColDisplaySortTitle = styled.span`
  display: none;
  font-weight: bold;
  margin-right: 4px;
  white-space: nowrap;

  ${(props) => props.theme.mediaQueries.md} {
    display: inline;
  }
`;

const ColDisplaySortSelect = styled.select`
  appearance: none;
  background: none;
  background-color: transparent;
  border: none;
  text-indent: 1px;
  margin-right: ${({ theme: { space } }) => space.s}px;
  margin-top: ${({ theme: { space } }) => space.xxs}px;
`;

const ColDisplayTypeContainer = styled.div`
  display: none;
  margin-left: 26px;

  ${(props) => props.theme.mediaQueries.md} {
    display: flex;
  }
`;

/* Display Type */
const getDisplayTypeIconColor = (props, isActive) =>
  isActive ? props.theme.colors.brand.primary : props.theme.colors.shade[3];

const ColDisplayTypeIcon = styled.div`
  width: 16px;
  height: 16px;
  display: none;
  position: relative;
  margin: 2px 6px 0;

  &:hover {
    cursor: pointer;
  }

  ${(props) => props.theme.mediaQueries.md} {
    display: block;
  }

  > div {
    background-color: ${(props) =>
      getDisplayTypeIconColor(props, props.isActive)};
  }
`;

const ColDisplayTypeIconGrid = styled(ColDisplayTypeIcon)`
  > div {
    position: absolute;
    width: 7px;
    height: 7px;

    :nth-child(1) {
      top: 0;
      left: 0;
    }
    :nth-child(2) {
      top: 0;
      left: 9px;
    }
    :nth-child(3) {
      top: 9px;
      left: 0;
    }
    :nth-child(4) {
      top: 9px;
      left: 9px;
    }
  }
`;
const ColDisplayTypeIconList = styled(ColDisplayTypeIcon)`
  > div {
    position: absolute;
    width: 16px;
    height: 7px;
    left: 0;

    :nth-child(1) {
      top: 0;
    }
    :nth-child(2) {
      top: 9px;
    }
  }
`;

const trackFilterDisplayType = (mode) =>
  dataLayer({
    eventAction: "click",
    eventCategory: "Filter",
    eventLabel: `DisplayType:${mode}`,
  });

const Toolbar = ({ sortTitle }) => {
  const {
    state: { filters, sortDirection, sortByName, displayType, collapseFilter },
    dispatch,
  } = useFilterProvider();

  const sortBy = [
    {
      label: "Empfehlungen",
      name: "recommendations",
      active: sortByName === "recommendations",
    },
    {
      label: "Preis",
      name: "price",
      active: sortByName === "price",
    },
  ];

  const selectedItem = _.find(sortBy, { active: true });

  const viewport = useViewport();

  return (
    <RowContainer>
      <div>
        <ColFilter>
          {viewport.lessThan.md && (
            <ColFilterIcon
              onClick={() => {
                dispatch({
                  type: SET_PARAM,
                  payload: {
                    collapseFilter: !collapseFilter,
                  },
                });
              }}
            >
              <FilterSvg />
              {Object.keys(filters).length > 0 && <ColFilterIconActivePoint />}
            </ColFilterIcon>
          )}
          Filter
        </ColFilter>
      </div>
      <div>
        <ColDisplay>
          <ColDisplaySortContainer>
            <Flex alignItems="center">
              <ColDisplaySortTitle>{sortTitle}</ColDisplaySortTitle>
              {"\u00A0"}
              <span>Empfehlungen</span>
              {false && (
                <ColDisplaySortSelect
                  onChange={(e) => {
                    dispatch({
                      type: SET_PARAM,
                      payload: {
                        sortByName: e.target.value,
                      },
                    });
                  }}
                  defaultValue={selectedItem.value}
                >
                  {sortBy.map((sortByEntry) => (
                    <option key={sortByEntry.name} value={sortByEntry.name}>
                      {sortByEntry.label}
                    </option>
                  ))}
                </ColDisplaySortSelect>
              )}
              {false && (
                <Flex
                  flexDirection="column"
                  onClick={() => {
                    const newSortDirection =
                      sortDirection === "desc" ? "asc" : "desc";
                    dispatch({
                      type: SET_PARAM,
                      payload: {
                        sortDirection: newSortDirection,
                      },
                    });
                    dataLayer({
                      eventAction: "click",
                      eventCategory: "Filter",
                      eventLabel: `Sort:${newSortDirection}`,
                    });
                  }}
                >
                  {(sortDirection === "asc" || sortDirection === "initial") && (
                    <Icon name="arrow-up" />
                  )}
                  {(sortDirection === "desc" ||
                    sortDirection === "initial") && <Icon name="arrow-down" />}
                </Flex>
              )}
            </Flex>
          </ColDisplaySortContainer>
          {false && (
            <ColDisplayTypeContainer>
              <ColDisplayTypeIconGrid
                isActive={displayType === "tile"}
                onClick={() => {
                  dispatch({
                    type: SET_PARAM,
                    payload: { displayType: "tile" },
                  });
                  trackFilterDisplayType(displayType);
                }}
              >
                {/* FIXME: Yes, we need these div-containers to build the icons */}
                <div />
                <div />
                <div />
                <div />
              </ColDisplayTypeIconGrid>
              <ColDisplayTypeIconList
                isActive={displayType === "row"}
                onClick={() => {
                  dispatch({
                    type: SET_PARAM,
                    payload: { displayType: "row" },
                  });
                  trackFilterDisplayType(displayType);
                }}
              >
                FIXME: Yes, we need these div-containers to build the icons
                <div />
                <div />
              </ColDisplayTypeIconList>
            </ColDisplayTypeContainer>
          )}
        </ColDisplay>
      </div>
    </RowContainer>
  );
};

Toolbar.propTypes = {
  sortTitle: PropTypes.string,
};

Toolbar.defaultProps = {
  sortTitle: "Sortiert nach",
};

export default Toolbar;
